import { License } from '@mprolab/sdk'

/** What a column sorts on. */
export type SortKey = 'id' | 'name' | 'type' | 'created' | 'acquired' | 'listed'
export type SortDirection = 'asc' | 'desc'

/**
 * The value a licence sorts by for a given column.
 *
 * Names compare without case, so `sam02` sits next to `Sam03` instead of after
 * every capitalised name. Dates compare as numbers, and an absent acquisition
 * date falls back to creation — for a licence that never changed hands those
 * two ARE the same day.
 */
const sortValue = (
  license: License,
  key: SortKey,
  listedTokenIds: Set<string>
): string | number => {
  switch (key) {
    // Listed first when ascending: the question this column answers is
    // "which ones did I put up for sale", so those are what the click is for.
    case 'listed':
      return license.tokenId && listedTokenIds.has(String(license.tokenId))
        ? 0
        : 1;
    case 'id':
      return license.licenseId ?? ''
    case 'name':
      return (license.name ?? license.licenseId ?? '').toLowerCase()
    case 'type':
      return license.type ?? ''
    case 'created':
      return new Date(license.purchasedAt).getTime()
    case 'acquired':
      return new Date(license.acquiredAt ?? license.purchasedAt).getTime()
  }
}

/**
 * Order the licence table.
 *
 * Returns a new array: the input comes from the query cache and reordering it
 * in place would change what every other reader of that cache sees.
 */
export const sortLicences = (
  licenses: License[],
  key: SortKey,
  direction: SortDirection,
  /** Tokens currently on the marketplace — only the `listed` column reads it. */
  listedTokenIds: Set<string> = new Set()
): License[] =>
  [...licenses].sort((a, b) => {
    const left = sortValue(a, key, listedTokenIds)
    const right = sortValue(b, key, listedTokenIds)
    if (left === right) return 0
    const order = left < right ? -1 : 1
    return direction === 'asc' ? order : -order
  })
